import { CommonHelperFunctions, ESdChangeReason } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { CustomTagProps } from "rc-select/lib/BaseSelect";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { IPureDesignComponentState, PureSdComponent } from "../../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { KuikaDropdownMenuItemType } from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSmallMediumText from "../../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import "./styling-selector.scss";
import { StylingTag } from "./styling-tag";

export enum EditMode {
  custom = 1,
  edit = 2,
  create = 3
}

interface IStylingSelector {
  formItemName: string[];
  stylingList: any;
  idField: string;
  defaultStylingRegex: RegExp;
  onChange?: (value: any) => void;
  handleSelectedTagRemoved: (label: string) => boolean;
  isDefaultStyling: (stylingId: string) => boolean;
  isCurrentComponentDefaultStyling: (stylingId: string) => boolean;
  stylingVersionMenu: (label: string, onClose?: () => void) => KuikaDropdownMenuItemType[];
  mode?: EditMode;
  new?: (name: string) => void;
  cancel?: () => void;
  save?: (name?: string) => void;
  form?: any;
  edit?: (name: string) => void;
  hide?: boolean;
}

interface IStylingState {
  searchText: string;
  editingTagValue: string | null;
  pendingNewStyle: string | null;
  isDropdownVisible: boolean;
  activeStylingMenuTag: string | null;
}

class StylingSelector extends PureSdComponent<
  IStylingSelector & IWithSDProps & IWithContextProp,
  IStylingState & IPureDesignComponentState
> {
  constructor(props: IStylingSelector & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      searchText: "",
      editingTagValue: null,
      pendingNewStyle: null,
      isDropdownVisible: false,
      activeStylingMenuTag: null
    };
  }

  validateStyleName = (styleName: string | undefined, isEditing?: boolean): string | null => {
    if (!styleName || styleName.trim().length === 0) {
      KMainFunctions.displayWarningNotification("Please enter a valid style name.");
      return null;
    }

    if (isEditing && this.state.editingTagValue) {
      const currentStyle = this.props.stylingList.find(
        (item) => item[this.props.idField] === this.state.editingTagValue
      );
      const currentStyleName = currentStyle?.name;

      const isNameExist = this.props.stylingList
        .filter((s) => s.name?.toLowerCase() !== currentStyleName?.toLowerCase())
        .map((s) => s.name?.toLowerCase())
        .includes(styleName.toLowerCase());

      if (isNameExist) {
        KMainFunctions.displayWarningNotification(
          `The style name "${styleName}" already exists. Please choose a different name.`
        );
        return null;
      }
    } else {
      const isNameExist = this.props.stylingList.map((s) => s.name?.toLowerCase()).includes(styleName.toLowerCase());

      if (isNameExist) {
        KMainFunctions.displayWarningNotification(
          `The style name "${styleName}" already exists. Please choose a different name.`
        );
        return null;
      }
    }

    return styleName;
  };

  setEditingTagValue = (value: string | null) => {
    this.setState({ editingTagValue: value });
  };

  tagRender = (props: CustomTagProps) => {
    const { label, closable, onClose, value } = props;

    const handleOnClose = () => {
      const success = this.props.handleSelectedTagRemoved(value);
      if (onClose && success) {
        onClose();
      }
      this.setState({ activeStylingMenuTag: null });
    };

    const handleStylingMenuVisibleChange = (visible: boolean) => {
      this.setState({
        activeStylingMenuTag: visible ? value : null,
        isDropdownVisible: visible ? false : this.state.isDropdownVisible
      });
    };

    return (
      <StylingTag
        label={label}
        value={value}
        closable={closable}
        onClose={handleOnClose}
        isDefaultStyling={this.props.isDefaultStyling}
        isCurrentComponentDefaultStyling={this.props.isCurrentComponentDefaultStyling}
        stylingVersionMenu={(label, onClose) => {
          const menu = this.props.stylingVersionMenu(label, onClose);
          const editMenuItem = menu.find((item: KuikaDropdownMenuItemType) =>
            typeof item.label === "string" ? item.label === "Edit style" : false
          );
          if (editMenuItem) {
            const originalOnClick = editMenuItem.onClick;
            editMenuItem.onClick = (e: any) => {
              this.setEditingTagValue(value);
              originalOnClick?.(e);
            };
          }
          return menu;
        }}
        mode={this.props.mode && this.state.editingTagValue === value ? this.props.mode : undefined}
        cancel={() => {
          this.setState({ editingTagValue: null });
          this.props.cancel?.();
        }}
        save={(newValue?: string) => {
          const validName = this.validateStyleName(newValue, true);
          if (!validName) return;

          this.setState({ editingTagValue: null });
          this.props.save?.(validName);
        }}
        isStylingMenuVisible={this.state.activeStylingMenuTag === value}
        setIsStylingMenuVisible={handleStylingMenuVisibleChange}
      />
    );
  };

  handleSearch = (value: string) => {
    this.setState({ searchText: value });
  };

  handleCreateNewStyle = async (styleName: string) => {
    if (!styleName) return;

    this.setState({ searchText: "", isDropdownVisible: false });

    if (this.props.new) {
      const validName = this.validateStyleName(styleName, false);
      if (!validName) return;

      await this.props.new(validName);

      setTimeout(() => {
        const newStyle = this.props.stylingList.find((item: any) => item.name === validName);
        this.setState({ editingTagValue: newStyle[this.props.idField] });
        this.props.edit?.(validName);
      }, 500);
    }
  };

  handleDropdownVisibleChange = (visible: boolean) => {
    this.setState({ isDropdownVisible: visible, activeStylingMenuTag: null });
  };

  render() {
    const { formItemName, stylingList, idField, defaultStylingRegex } = this.props;

    const { searchText, isDropdownVisible } = this.state;
    const dropdownHeader = searchText ? (
      <div className="kuika-styling-selector__dropdown-header--wrapper">
        <span>Add</span>
        <div className="kuika-styling-selector__class-name kuika-styling-selector__class-name__default kuika-styling-selector__dropdown-header--text">
          <KuikaSmallMediumText style={{ textTransform: "none", width: "100%" }}>{searchText}</KuikaSmallMediumText>
        </div>
      </div>
    ) : (
      "Search style or create new"
    );

    return (
      <div className="kuika-styling-selector" style={{ display: this.props.hide ? "none" : "block" }}>
        <KuikaSmallMediumText>Style</KuikaSmallMediumText>
        <Form.Item name={formItemName} noStyle shouldUpdate>
          <KuikaSelectbox
            mode="tags"
            handleKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();

                const currentSearchText = searchText;
                if (currentSearchText) {
                  this.handleCreateNewStyle(currentSearchText);
                }
              } else if (e.key === "Backspace" && !searchText) {
                e.preventDefault();
                const selectedValues = this.props.form?.getFieldValue(formItemName);
                if (selectedValues && selectedValues.length > 0) {
                  const lastTag = selectedValues[selectedValues.length - 1];
                  if (lastTag) {
                    const success = this.props.handleSelectedTagRemoved(lastTag);
                    if (success) {
                      const formValues = {};
                      formValues[formItemName[0]] = {
                        ...this.props.form?.getFieldValue(formItemName[0]),
                        [formItemName[1]]: selectedValues.slice(0, -1)
                      };
                      this.props.form?.setFieldsValue(formValues);
                      this.props.form?.submit();
                    }
                  }
                }
              }
            }}
            tagRender={this.tagRender}
            searchable
            onSearch={this.handleSearch}
            searchValue={searchText}
            tokenSeparators={[]}
            preventTagCreation={true}
            onChange={(value: string | number | Array<string | number> | null) => {
              let _value = value;
              if (!_value || !Array.isArray(_value) || _value.length === 0) return;

              const lastSelectedValue = _value[_value.length - 1];

              const styleName = this.state.searchText;
              const isDuplicate = this.props.stylingList.some(
                (style) => style.name?.toLowerCase() === styleName.toLowerCase()
              );
              if (
                isDuplicate &&
                typeof lastSelectedValue === "string" &&
                !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(lastSelectedValue)
              ) {
                KMainFunctions.displayWarningNotification(
                  `Style "${styleName}" already exists. Please choose a different name.`
                );
                this.setState({ searchText: "" });
                _value = _value.slice(0, -1);
                this.props.onChange?.(_value);
                return;
              }

              let existingStyle = stylingList?.find(
                (item) => item[idField]?.toLowerCase() === String(lastSelectedValue).toLowerCase()
              );

              if (!existingStyle) {
                const matchedByName = stylingList?.find(
                  (item) => item.name?.toLowerCase() === String(lastSelectedValue).toLowerCase()
                );
                // Only a style that actually HAS an id can be attached. The slot used to be
                // overwritten with `matchedByName[idField]` unguarded, so a style carrying no id
                // wrote `undefined` into the chain — and `undefined` inside an array serialises to
                // `null`, which is how a saved chain ends up holding a null that the styling
                // panel then has to defend against (see isUsableStyleId in
                // unified-styling-selector). Without an id, treat it as not found.
                const matchedId: string | undefined = matchedByName?.[idField];
                if (matchedId) {
                  existingStyle = matchedByName;
                  _value[_value.length - 1] = matchedId;
                }
              }

              if (existingStyle) {
                this.props.onChange?.(_value);
              } else if (this.state.searchText) {
                this.handleCreateNewStyle(this.state.searchText);
              }

              this.setState({ searchText: "" });
            }}
            options={
              stylingList?.length > 0
                ? stylingList
                    .sort((a, b) => CommonHelperFunctions.compareObjectWithField(a, b, "name"))
                    .filter(
                      (item) =>
                        item.name &&
                        item[idField] &&
                        item.name?.match(defaultStylingRegex) == null &&
                        (!searchText ||
                          (item.name.toLowerCase().includes(searchText.toLowerCase()) &&
                            item.name.toLowerCase() !== searchText.toLowerCase()))
                    )
                    .map((item) => ({
                      value: item[idField]?.toLowerCase(),
                      label: item.name
                    }))
                : []
            }
            filterValue={(value) =>
              !this.props.isCurrentComponentDefaultStyling(String(value)) && !String(value)?.match(defaultStylingRegex)
            }
            dropdownHeader={dropdownHeader}
            onDropdownHeaderClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              const currentSearchText = searchText;
              if (currentSearchText) {
                this.handleCreateNewStyle(currentSearchText);
              }
            }}
            dropdownHeaderClassName="kuika-styling-selector__dropdown-header"
            visible={isDropdownVisible}
            onVisibleChange={this.handleDropdownVisibleChange}
          />
        </Form.Item>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.ChangePropertyValue ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeStyleValue ||
      changeReason === ESdChangeReason.ChangeStyleValueExternal ||
      changeReason === ESdChangeReason.ChangeProjectStyle
    );
  };
}

const stylingSelectorComponent = withContext<IStylingSelector>(withSD<IStylingSelector>(StylingSelector));
export { stylingSelectorComponent as StylingSelector };
